/**
 * Portal Password Gate (Story 14.3 — Task 9.2 / AC #10 / AC #14)
 *
 * Rendered when the meta endpoint returns `passwordProtected === true` and the
 * viewer has not yet verified a password for this link. On successful verify,
 * the parent component refreshes the meta query to re-check the session cookie.
 *
 * Rate-limit handling (AC #10): a 429 response surfaces an inline countdown
 * parsed from the `Retry-After` header. Submit button is disabled while the
 * countdown is live.
 *
 * UI Design System compliance:
 *   - max-w-md centered card with rounded-3xl and 0.5px border
 *   - Outline-variant submit button (text-[13px], hover -translate-y-0.5)
 *   - Staggered fade-in at 50ms per form field (password + submit)
 */

import { FormEvent, useEffect, useRef, useState } from "react";
import { Button, Input, Label } from "@provenleap/ui";
import {
  PortalApiError,
  verifyPortalPassword,
  type VerifyPortalPasswordResult,
} from "../../lib/portalPublicApi";

/**
 * Backend seam (additive, default-preserving).
 *
 * PortalPasswordGate is reused by both public surfaces (report portal +
 * dashboard share view). The default backend verifies against
 * `/api/portal-view/:token/verify-password`; the dashboard route supplies its
 * own backend (dashboardShareApi) hitting `/api/dashboard-share-view/*`. An
 * omitted `api` prop is byte-identical to the pre-seam report-portal behavior.
 *
 * `isTimeoutError` is a predicate because each backend throws its OWN error
 * class (PortalApiError vs DashboardShareApiError) — a single `instanceof` would
 * miss the other surface's timeout.
 */
export interface PortalPasswordGateBackend {
  verifyPassword(
    token: string,
    password: string,
  ): Promise<VerifyPortalPasswordResult>;
  /** True when the thrown error represents a request timeout. */
  isTimeoutError(error: unknown): boolean;
}

/** Default backend — the report portal (`/api/portal-view/*`). Byte-identical to pre-seam behavior. */
const DEFAULT_PASSWORD_GATE_BACKEND: PortalPasswordGateBackend = {
  verifyPassword: (token, password) => verifyPortalPassword(token, password),
  isTimeoutError: (error) =>
    error instanceof PortalApiError && error.kind === "timeout",
};

export interface PortalPasswordGateProps {
  /** The raw portal token from the URL — used to call verify-password */
  token: string;
  /** Tenant's label for the link — used to give the viewer context */
  label: string;
  /** Client name — shown above the password input */
  clientName: string;
  /** Agency logo URL (nullable); when null, fallback to companyName / clientName (or ProvenLeap for free tier) */
  logoUrl: string | null;
  /** Agency display name (Story 15.7) — the white-label wordmark fallback for paid tiers */
  companyName: string | null;
  /** Whether ProvenLeap branding shows (free tier only, Story 15.7). Same flag as the portal footer. */
  poweredByProvenLeap: boolean;
  /** Called after successful password verify — parent should refresh meta query */
  onVerified: () => void;
  /**
   * Optional backend seam — defaults to the report portal
   * (`/api/portal-view/:token/verify-password`). The dashboard share route
   * passes the dashboard backend so the password verifies against
   * `/api/dashboard-share-view/*`. Omit for byte-identical report-portal behavior.
   */
  api?: PortalPasswordGateBackend;
}

export function PortalPasswordGate({
  token,
  label,
  clientName,
  logoUrl,
  companyName,
  poweredByProvenLeap,
  onVerified,
  api = DEFAULT_PASSWORD_GATE_BACKEND,
}: PortalPasswordGateProps) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [retryAfter, setRetryAfter] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Story 15.7: only free-tier shows the ProvenLeap suffix; paid tiers use the
  // agency company name (then the client name) — never ProvenLeap.
  const wordmark = poweredByProvenLeap ? "ProvenLeap" : (companyName ?? clientName);

  useEffect(() => {
    inputRef.current?.focus();
    const prev = document.title;
    document.title = poweredByProvenLeap
      ? `Password required — ${clientName} — ProvenLeap`
      : `Password required — ${companyName ?? clientName}`;
    return () => {
      document.title = prev;
    };
  }, [clientName, companyName, poweredByProvenLeap]);

  // Retry-After countdown tick
  useEffect(() => {
    if (retryAfter === null) return;
    if (retryAfter <= 0) {
      setRetryAfter(null);
      return;
    }
    const id = window.setTimeout(() => setRetryAfter((v) => (v === null ? null : v - 1)), 1000);
    return () => window.clearTimeout(id);
  }, [retryAfter]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting || retryAfter !== null) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await api.verifyPassword(token, password);
      if (result.status === 429) {
        setRetryAfter(result.retryAfter ?? 60);
        setError(
          `Too many attempts. Please wait ${result.retryAfter ?? 60} seconds before trying again.`
        );
        return;
      }
      if (result.status === 200 && "verified" in result.body && result.body.verified) {
        onVerified();
        return;
      }
      setError("Incorrect password. Please try again.");
      setPassword("");
      inputRef.current?.focus();
    } catch (err) {
      if (api.isTimeoutError(err)) {
        setError("The request timed out. Please try again.");
      } else {
        setError("Unable to verify password. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const disabled = submitting || retryAfter !== null || password.length === 0;

  return (
    <main
      id="main-content"
      aria-labelledby="portal-password-heading"
      className="min-h-screen flex items-center justify-center px-6 py-12 bg-background"
    >
      <div className="w-full max-w-md rounded-3xl border-[0.5px] border-[var(--hairline)] bg-card p-8 shadow-xs">
        {/* Header */}
        <div
          className="mb-6 flex flex-col items-center animate-in fade-in duration-300"
          style={{ animationDelay: "0ms" }}
        >
          {logoUrl ? (
            // Security: SVG branding logos MUST render via <img src>, never
            // <object>/<iframe>/<embed>/inlined. See Story 14.5 AC #4 +
            // CVE-2026-33172 pattern.
            <img
              src={logoUrl}
              alt=""
              className="h-10 w-auto mb-4"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="mb-4 text-sm font-medium tracking-tight text-muted-foreground">
              {wordmark}
            </div>
          )}
          <h1
            id="portal-password-heading"
            className="text-xl font-semibold tracking-tight text-center"
          >
            Password required
          </h1>
          <p className="mt-1 text-[13px] text-muted-foreground text-center">
            {clientName} · {label}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4" aria-describedby={error ? "portal-password-error" : undefined}>
          <div
            className="space-y-2 animate-in fade-in duration-300"
            style={{ animationDelay: "50ms" }}
          >
            <Label htmlFor="portal-password-input" className="text-[13px] font-medium">
              Password
            </Label>
            <Input
              id="portal-password-input"
              ref={inputRef}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting || retryAfter !== null}
              required
              maxLength={256}
            />
          </div>

          {error !== null && (
            <div
              id="portal-password-error"
              role="alert"
              className="text-[13px] text-destructive"
            >
              {error}
            </div>
          )}

          <div
            className="pt-2 animate-in fade-in duration-300"
            style={{ animationDelay: "100ms" }}
          >
            <Button
              type="submit"
              variant="outline"
              className="w-full text-[13px] transition-transform hover:-translate-y-0.5 hover:shadow-sm"
              disabled={disabled}
            >
              {submitting
                ? "Verifying…"
                : retryAfter !== null
                  ? `Wait ${retryAfter}s`
                  : "Unlock dashboard"}
            </Button>
          </div>
        </form>
      </div>
    </main>
  );
}
